'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Star, Share2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { Amenity, Rating, Workspace } from 'hotdesk_typescript';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { LanguageCode } from '@/hotdesk-ui/constants/langulage.constant';
import { Status } from '@/hotdesk-ui/enum/status-keys';
import ErroModalClient from '@/hotdesk-ui/components/error-modal/ErrorModalClient';
import WorkspaceDiscoveryTracking from '@/hotdesk-ui/components/tracking/WorkspaceDiscoveryTracking';
import CarouselSection from '@/hotdesk-ui/components/shared/CarouselSection';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import InactiveWorkspaceView from './InactiveWorkspaceView';
const ShareModal = dynamic(() => import('./ShareModal'), {
  ssr: false
});
const BusinessForm = dynamic(() => import('@/hotdesk-ui/components/businessForm/BusinessFormClient'), {
  ssr: false
});
const RequestVisitModal = dynamic(() => import('@/hotdesk-ui/components/request-visit-modal/RequestVisitModalClient'), {
  ssr: false
});
import ImageGallery from './ImageGallery';
import ProviderLabel from '@/hotdesk-ui/components/booking-v4/ProviderLabel';
import StickyNav, { StickyNavTab } from './StickyNav';
import AboutSection from './AboutSection';
import AmenitiesGrid from './AmenitiesGrid';
import ReviewsSection from './ReviewsSection';
import LocationSection from './LocationSection';
import PromoBanner from './PromoBanner';
import BookingSidebar from './BookingSidebar';
import MobileStickyBar from './MobileStickyBar';
import { parseRatingFromMetaData } from '@/hotdesk-ui/helpers/rating.utils';
import { useWebReviewsDisplay } from '@/hotdesk-ui/hooks/useWebReviewsDisplay';
import { getCitySlug } from '@/hotdesk-ui/helpers/workspace-url';
import styles from './WorkspaceDetailsClient.module.scss';
import { RootState } from '@/hotdesk-ui/store/store';
import ContactUs from '@/hotdesk-ui/components/details-v4/ContactUsOptions';
import WorkspaceListings from '@/hotdesk-ui/components/popup/workspace-listing/WorkspaceListings';
import { useSubscriberSimilarListings } from '@/hotdesk-ui/hooks/useListingMemberData';
export interface WorkspaceDetailsClientProps {
  workspaceDetail: Workspace;
  listings: MyListing[];
  amenities: Amenity[];
  reviews: Rating[];
  /** API total of published reviews; `reviews` is only the first page. */
  reviewsTotal?: number;
  similarWorkspaces: MyListing[];
  overAllRating: number;
  ratingCount: number;
  ratingCriteria: any[];
  ratingCriteriaAverage: any[];
  languageCode: LanguageCode;
  workspaceListingCount: number;
}
const PRICE_ORDER = ['hour', 'day', 'month', 'annual'];
const getStartingPrice = (listings: MyListing[]) => {
  for (const unit of PRICE_ORDER) {
    for (const l of listings) {
      if (l.rules?.price?.[unit]) {
        return {
          price: l.rules.price[unit],
          unit,
          currency: l.rules?.currency || ''
        };
      }
    }
  }
  return null;
};
const getWorkingHoursToday = (workingHours: Record<string, string> | undefined) => {
  if (!workingHours) return {
    dayLabel: '',
    hours: ''
  };
  const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const dayIndex = new Date().getDay();
  const todayKey = days[dayIndex];
  if (workingHours[todayKey]) return {
    dayLabel: 'today',
    hours: workingHours[todayKey]
  };
  let nextIdx = (dayIndex + 1) % 7;
  while (nextIdx !== dayIndex) {
    const key = days[nextIdx];
    if (workingHours[key]) {
      return {
        dayLabel: nextIdx === (dayIndex + 1) % 7 ? 'tomorrow' : key,
        hours: workingHours[key]
      };
    }
    nextIdx = (nextIdx + 1) % 7;
  }
  return {
    dayLabel: '',
    hours: ''
  };
};
const WorkspaceDetailsClient: FC<WorkspaceDetailsClientProps> = ({
  workspaceDetail,
  listings,
  amenities,
  reviews,
  reviewsTotal,
  similarWorkspaces: baseSimilarWorkspaces,
  overAllRating,
  ratingCount,
  workspaceListingCount,
  languageCode
}) => {
  const t = useTranslations('workspace-details');
  const router = useRouter();

  // Subscriber-filtered nearby workspaces are fetched client-side — never
  // rendered on the server (see useListingMemberData).
  const similarWorkspacesFilters = useMemo(() => workspaceDetail?.location?.coordinates ? {
    loc: [workspaceDetail.location.coordinates[1], workspaceDetail.location.coordinates[0], 15].toString(),
    status: '1',
    workspaceStatus: 1,
    limit: 12,
    distinct: 'workspace'
  } : null, [workspaceDetail]);
  const similarWorkspaces = useSubscriberSimilarListings(baseSimilarWorkspaces, similarWorkspacesFilters, workspaceDetail?.id, 'workspace', languageCode);
  const galleryRef = useRef<HTMLDivElement>(null);
  const amenitiesRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const listingsRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const showReviews = useWebReviewsDisplay('workspace_details');
  const [activeTab, setActiveTab] = useState('amenities');
  const [shareOpen, setShareOpen] = useState(false);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [requestVisitOpen, setRequestVisitOpen] = useState(false);
  const [contactUsBoxOpen, setcontactUsBoxOpen] = useState(false);
  const tabs: StickyNavTab[] = [{
    id: 'amenities',
    label: t('amenities')
  }, {
    id: 'location',
    label: t('location_title')
  }, ...(listings.length > 0 ? [{
    id: 'listings',
    label: t('listings')
  }] : []), ...(showReviews && reviews.length > 0 ? [{
    id: 'reviews',
    label: t('reviews')
  }] : [])];
  const sectionRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
    amenities: amenitiesRef,
    location: locationRef,
    listings: listingsRef,
    reviews: reviewsRef
  };
  const handleTabClick = (id: string) => {
    const ref = sectionRefs[id];
    if (!ref?.current) return;

    // On mobile, the listing cards' "View" CTA sits at the card bottom,
    // and a section-top anchor often leaves it hidden behind the sticky
    // bar. For the listings tab on mobile, anchor to the section bottom
    // (offset above the sticky bar) so the CTAs stay visible.
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 991;
    if (id === 'listings' && isMobile) {
      const rect = ref.current.getBoundingClientRect();
      const stickyBarOffset = 140;
      const top = rect.bottom + window.scrollY - window.innerHeight + stickyBarOffset;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth'
      });
      return;
    }
    const top = ref.current.getBoundingClientRect().top + window.scrollY - 200;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  const amenitiesMap = useAppSelector((state: RootState) => state.amenities.items);
  const amenityIds = workspaceDetail.amenities ? (Array.isArray(workspaceDetail.amenities) ? workspaceDetail.amenities : Array.from(workspaceDetail.amenities as Iterable<number>)) as number[] : [];
  const resolvedAmenities = amenityIds.map(id => amenitiesMap[id]).filter(Boolean);
  const hasAmenities = resolvedAmenities.length > 0;
  const title = hasAmenities ? `${workspaceDetail?.title} in ${workspaceDetail?.workspaceCity?.name} showing ${resolvedAmenities[0]?.name}${resolvedAmenities[1] ? `, ${resolvedAmenities[1].name}` : ''}` : `${workspaceDetail?.title} in ${workspaceDetail?.workspaceCity?.name}`;

  // Track active tab based on scroll position
  useEffect(() => {
    const entries = Object.entries(sectionRefs);
    const observer = new IntersectionObserver(observed => {
      for (const entry of observed) {
        if (entry.isIntersecting) {
          const id = entries.find(([, ref]) => ref.current === entry.target)?.[0];
          if (id) setActiveTab(id);
        }
      }
    }, {
      rootMargin: '-120px 0px -60% 0px',
      threshold: 0
    });
    entries.forEach(([, ref]) => {
      if (ref.current) observer.observe(ref.current);
    });
    return () => observer.disconnect();
  }, [listings.length, reviews.length]);
  const handleShare = () => {
    setShareOpen(true);
  };
  const handleContactSales = () => {
    setContactFormOpen(true);
  };
  const handleRequestViewing = () => {
    setRequestVisitOpen(true);
  };
  const handleContactUs = () => {
    setcontactUsBoxOpen(true);
  };
  const manageContactSales = () => {
    setcontactUsBoxOpen(false);
    setContactFormOpen(true);
  };
  const handleSubscriptionClick = () => {
    router.push('/memberships');
  };
  const startingPrice = getStartingPrice(listings);
  const subscriptionApplicable = listings.some(l => l.metaData?.subscriptionApplicable);
  const workspaceRating = parseRatingFromMetaData(workspaceDetail?.metaData);
  const effectiveOverallRating = workspaceRating.overall || overAllRating;
  const effectiveRatingCount = workspaceRating.count || ratingCount;
  const rawRating = workspaceRating.displayRating ?? (effectiveOverallRating > 0 ? (effectiveOverallRating / 100 * 5).toFixed(1) : null);
  const capacity = listings.reduce((sum, l) => sum + ((l as any).capacity || 0), 0);
  const workingHoursToday = getWorkingHoursToday(workspaceDetail?.rules?.workingHours);
  const [workspaceListings, setWorkspaceListings] = useState<{
    id: number;
    name: string;
  } | null>(null);
  if (!workspaceDetail) return null;

  // Show inactive workspace view if workspace is not active
  if (workspaceDetail.status === Status.Inactive) {
    return <InactiveWorkspaceView workspace={workspaceDetail} similarWorkspaces={similarWorkspaces} />;
  }
  return <div className={styles.page} data-sentry-component="WorkspaceDetailsClient" data-sentry-source-file="WorkspaceDetailsClient.tsx">
            {workspaceDetail.id != null && <WorkspaceDiscoveryTracking workspaceId={workspaceDetail.id} workspaceName={workspaceDetail.title ?? ''} workspaceAmenities={amenities.map(a => a.name).filter(Boolean)} listingIds={listings.map(l => l.id).filter((id): id is number => id != null)} />}

            {/* Image Gallery */}
            <div ref={galleryRef} className={styles.galleryWrap}>
                <ImageGallery images={workspaceDetail?.rules?.images || {}} title={title || ''} data-sentry-element="ImageGallery" data-sentry-source-file="WorkspaceDetailsClient.tsx" />
            </div>

            {/* Two-column container: left = all content, right = sticky sidebar */}
            <div className={styles.container}>
                {/* Left column */}
                <div className={styles.mainCol}>
                    {/* Header: Title, Stats */}
                    <div className={styles.headerSection}>
                        <div className={styles.titleRow}>
                            <h1 className={styles.title}>{workspaceDetail?.title}</h1>
                            <div className={styles.actions}>
                                <button type="button" className={styles.actionBtn} aria-label="Share" onClick={handleShare}>
                                    <Share2 size={20} data-sentry-element="Share2" data-sentry-source-file="WorkspaceDetailsClient.tsx" />
                                </button>
                            </div>
                        </div>

                        <div className={styles.statsRow}>
                            {showReviews && rawRating && <span className={styles.statItem}>
                                    <Star size={14} fill="#000" color="#000" />
                                    {rawRating}
                                    {effectiveRatingCount > 0 && ` (${effectiveRatingCount})`}
                                </span>}
                            <ProviderLabel listing={workspaceDetail as unknown as Record<string, unknown>} variant="subtle" data-sentry-element="ProviderLabel" data-sentry-source-file="WorkspaceDetailsClient.tsx" />
                        </div>
                    </div>

                    {/* Sticky Nav — scroll to section */}
                    <StickyNav tabs={tabs} activeTab={activeTab} onTabClick={handleTabClick} data-sentry-element="StickyNav" data-sentry-source-file="WorkspaceDetailsClient.tsx" />

                    {/* Promo + About */}
                    <PromoBanner subscriptionApplicable={subscriptionApplicable} showCustomQuote={true} onSubscriptionClick={handleSubscriptionClick} onCustomQuoteClick={handleRequestViewing} data-sentry-element="PromoBanner" data-sentry-source-file="WorkspaceDetailsClient.tsx" />
                    <AboutSection description={(workspaceDetail as Workspace & {
          descriptions?: Partial<Record<LanguageCode, string>>;
        }).descriptions?.[languageCode] || workspaceDetail?.rules?.description || ''} data-sentry-element="AboutSection" data-sentry-source-file="WorkspaceDetailsClient.tsx" />

                    {/* Amenities section */}
                    <div ref={amenitiesRef} id="amenities">
                        <AmenitiesGrid amenities={amenities} data-sentry-element="AmenitiesGrid" data-sentry-source-file="WorkspaceDetailsClient.tsx" />
                    </div>

                    {/* Location section */}
                    <div ref={locationRef} id="location">
                        <LocationSection workspace={workspaceDetail} cardItem={listings[0] ? {
            ...listings[0],
            workspace: workspaceDetail
          } as any : undefined} cardType="workspace" cardUrl={listings[0] ? `/workspaces/${(listings[0] as any)?.city?.country?.slug?.toLowerCase()}/${getCitySlug((listings[0] as any)?.city || workspaceDetail?.workspaceCity)}/${workspaceDetail?.slug}` : undefined} data-sentry-element="LocationSection" data-sentry-source-file="WorkspaceDetailsClient.tsx" />
                    </div>
                </div>

                {/* Right column — sticky sidebar */}
                <aside className={styles.sideCol}>
                    <BookingSidebar variant="workspace" listing={startingPrice ? {
          rules: {
            price: {
              [startingPrice.unit]: startingPrice.price
            },
            currency: startingPrice.currency,
            workingHours: workspaceDetail?.rules?.workingHours
          }
        } : undefined} onViewListings={() => handleTabClick('listings')} onContactSales={handleContactSales} data-sentry-element="BookingSidebar" data-sentry-source-file="WorkspaceDetailsClient.tsx" />
                </aside>
            </div>

            {/* Full-width sections below the two-col container */}
            {listings.length > 0 && <div ref={listingsRef} id="listings" className={styles.fullWidthSection}>
                    <CarouselSection title={t('workspaceListings')} listings={listings as any} variant="listing" showBookNow workspaceListingCount={workspaceListingCount} onWorkspacesClick={() => setWorkspaceListings({
        id: workspaceDetail?.id || 0,
        name: workspaceDetail?.title || ''
      })} />
                </div>}
            {workspaceListings && <WorkspaceListings showPopup={!!workspaceListings} workspaceId={workspaceListings.id} workspaceName={workspaceListings.name} languageCode={languageCode} onClose={() => setWorkspaceListings(null)} showBookNow />}

            {showReviews && reviews.length > 0 && <div ref={reviewsRef} id="reviews" className={styles.fullWidthSection}>
                    <ReviewsSection reviews={reviews} reviewsTotal={reviewsTotal} overAllRating={effectiveOverallRating} ratingCount={workspaceRating.count || ratingCount} trackingSource="workspace_details" workspaceId={workspaceDetail?.id} />
                </div>}

            {similarWorkspaces.length > 0 && <div className={styles.fullWidthSection}>
                    <CarouselSection title={t('similarWorkspaces')} listings={similarWorkspaces as any} variant="workspace" />
                </div>}

            {/* Mobile Sticky Bar */}
            <MobileStickyBar variant="workspace" visible listing={startingPrice ? {
      rules: {
        price: {
          [startingPrice.unit]: startingPrice.price
        },
        currency: startingPrice.currency,
        workingHours: workspaceDetail?.rules?.workingHours
      }
    } : undefined} onViewListings={() => handleTabClick('listings')} onContactUs={handleContactUs} workingHoursToday={workingHoursToday} data-sentry-element="MobileStickyBar" data-sentry-source-file="WorkspaceDetailsClient.tsx" />

            {/* Share Modal */}
            <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} variant="workspace" item={workspaceDetail} data-sentry-element="ShareModal" data-sentry-source-file="WorkspaceDetailsClient.tsx" />

            {/* Contact Form Modal */}
            {contactFormOpen && <div className={styles.modalOverlay} onClick={() => setContactFormOpen(false)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <BusinessForm onClose={() => setContactFormOpen(false)} workspaceId={workspaceDetail?.id} />
                    </div>
                </div>}
            <RequestVisitModal open={requestVisitOpen} onClose={() => setRequestVisitOpen(false)} languageCode={languageCode} listingId={workspaceDetail?.id} workspaceId={workspaceDetail?.id} workspaceName={workspaceDetail?.title ?? ''} source="workspace_details" data-sentry-element="RequestVisitModal" data-sentry-source-file="WorkspaceDetailsClient.tsx" />
            {contactUsBoxOpen && <div className={styles.modalOverlay} onClick={() => setcontactUsBoxOpen(false)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <ContactUs listing={startingPrice ? {
          rules: {
            price: {
              [startingPrice.unit]: startingPrice.price
            },
            currency: startingPrice.currency,
            workingHours: workspaceDetail?.rules?.workingHours
          }
        } : undefined} manageContactSales={manageContactSales} onClose={() => setcontactUsBoxOpen(false)} />
                    </div>
                </div>}

            <ErroModalClient show={false} data-sentry-element="ErroModalClient" data-sentry-source-file="WorkspaceDetailsClient.tsx" />
        </div>;
};
export default WorkspaceDetailsClient;